import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Heart, Loader2, HeartOff } from 'lucide-react';
import { favoritesApi } from '../lib/api.js';
import { useAuthStore, useUiStore, usePlayerStore } from '../lib/stores.js';
import styles from './FavoritesPage.module.css';

export default function FavoritesPage() {
  const token = useAuthStore((s) => s.token);
  const openModal = useUiStore((s) => s.openModal);
  const selectChannel = usePlayerStore((s) => s.selectChannel);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const favoritesQ = useQuery({
    queryKey: ['favorites', 'channel'],
    queryFn: () => favoritesApi.list('channel'),
    enabled: !!token,
  });

  const remove = useMutation({
    mutationFn: (favorite_id) => favoritesApi.remove(favorite_id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['favorites', 'channel'] }),
  });

  if (!token) {
    return (
      <div className={styles.center}>
        <Heart size={36} />
        <h2>Sign in to see your favorites</h2>
        <button className="btn primary" onClick={() => openModal('login')}>Sign in</button>
      </div>
    );
  }

  if (favoritesQ.isLoading) return <div className={styles.center}><Loader2 className={styles.spin} /></div>;

  const items = favoritesQ.data?.results || [];

  if (items.length === 0) {
    return (
      <div className={styles.center}>
        <HeartOff size={36} />
        <h2>No favorite channels yet</h2>
        <p className="muted">Tap the heart next to any channel to save it here.</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1><Heart size={22} /> Favorites</h1>
        <p className="muted">{items.length} saved {items.length === 1 ? 'channel' : 'channels'}</p>
      </header>
      <div className={styles.grid}>
        {items.map((fav) => {
          const ch = fav.item;
          if (!ch) return null;
          return (
            <article key={fav.id} className={styles.card} onClick={() => {
              selectChannel(ch);
              navigate('/');
            }}>
              <div className={styles.logo}>
                {ch.logo_url
                  ? <img src={ch.logo_url} alt="" />
                  : <span>{(ch.name || '?').slice(0, 2).toUpperCase()}</span>}
              </div>
              <div className={styles.body}>
                <strong>{ch.name}</strong>
                <span className="muted">{ch.category?.name || ch.country || 'Live channel'}</span>
              </div>
              <button
                className={styles.remove}
                aria-label="Remove favorite"
                onClick={(e) => { e.stopPropagation(); remove.mutate(fav.id); }}
                disabled={remove.isPending}
              >
                <Heart size={16} fill="currentColor" />
              </button>
            </article>
          );
        })}
      </div>
    </div>
  );
}
